'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import ImpactMetrics from './ImpactMetrics';
import ParticipationMap from './ParticipationMap';
import TransformationStories from './TransformationStories';
import FundingAllocation from './FundingAllocation';
import ResearchPartners from './ResearchPartners';
import MediaCoverage from './MediaCoverage';
import MembershipSection from '@/app/donate/components/MembershipSection';
import VolunteerSection from '@/app/humanitarian-action/components/VolunteerSection';
import MovementRegistrationPopup from '@/components/common/MovementRegistrationPopup';

const GlobalImpactInteractive: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);

  const handleViewStory = (storyId: number) => {
    // Navigate to story details or show modal
    alert(`Viewing Transformation Story #${storyId}\n\nIn a full implementation, this would:\n\n• Display the complete transformation story\n• Show before/after impact metrics\n• Include photos and testimonials\n• Provide sharing options\n• Show related stories\n\nThese stories demonstrate the real-world impact of the SIG Day movement on individuals and communities.`);
    // In production, this could navigate to /community-stories?id={storyId}
    // or open a modal with full story details
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-7xl mx-auto space-y-16">
          {/* Hero Section */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground">
              Global Impact Dashboard
            </h1>
            <p className="text-lg md:text-xl font-body text-muted-foreground max-w-3xl mx-auto">
              Real-time tracking of the SIG Day movement's transformative effect on communities,
              individuals, and humanitarian outcomes worldwide
            </p>
          </div>

          {/* Impact Metrics */}
          <section>
            <ImpactMetrics />
          </section>

          {/* Participation Map */}
          <section>
            <ParticipationMap />
          </section>

          {/* Transformation Stories */}
          <section>
            <TransformationStories onViewStory={handleViewStory} />
          </section>

          {/* Funding Allocation */}
          <section>
            <FundingAllocation />
          </section>

          {/* Research Partners */}
          <section>
            <ResearchPartners />
          </section>

          {/* Media Coverage */}
          <section>
            <MediaCoverage />
          </section>

          {/* Join the Global Peace Movement Section */}
          <section>
            <MembershipSection />
          </section>

          <section>
            <VolunteerSection />
          </section>

          {/* CTA Section */}
          <section className="bg-primary rounded-lg p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-primary-foreground mb-4">
              Be Part of the Transformation
            </h2>
            <p className="text-lg font-body text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Join millions worldwide choosing compassion over hostility. Your journey to living
              with no enemy begins today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setShowPopup(true)}
                className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta text-base font-bold hover:scale-105 transition-transform duration-200 pulse-gentle"
              >
                Start Your 30-Day Journey
              </button>
              <Link href="/donate" className="px-8 py-3 bg-primary-foreground text-primary rounded-md font-cta text-base font-bold hover:bg-primary-foreground/90 transition-colors duration-200">
                Support Humanitarian Action
              </Link>
            </div>
          </section>
        </div>
      </div>

      {showPopup && (
        <MovementRegistrationPopup
          sourcePage="global-impact-cta"
          isOpen={showPopup}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
};

export default GlobalImpactInteractive;